import { CssBaseline, ThemeProvider } from '@mui/material';
import { lazy } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import useFistoTheme from './hooks/useFistoTheme.js';

import { SuspenseWrapper } from './components/SuspenseWrapper.jsx';
import FistoProvider from './contexts/FistoContext';
import ReasonProvider from './contexts/ReasonContext';
import NotFound from './exceptions/NotFound.jsx';
import Sandbox from './Sandbox';

// Experimental Refactored Transction Request
import applicationRouteConfig from './config/application-route.config.js';
import approvingRouteConfig from './config/approving-route.config.js';
import auditingRouteConfig from './config/auditing-route.js';
import chequeRouteConfig from './config/cheque-route.config.js';
import confidentialRequestRouteConfig, { confidentialRouteConfig } from './config/confidential-route.config.js';
import counteringRouteConfig from './config/countering-route.config.js';
import documentsRouteConfig from './config/documents-route.config.js';
import generalJournalRouteConfig, { accrualReversalRouteConfig } from './config/general-journal-route.config.js';
import masterlistConfig from './config/masterlist-route.config.js';
import reportRouteConfig from './config/reports-route.config.js';
import requestConfig, { managersChequeRequestConfig } from './config/request-route.config.js';
import voucheringRouteConfig from './config/vouchering-route.config.js';
import AccrualFolders from './routes/general journal/accrual-reversal/folders/AccrualFolders.jsx';
import AccrualMonth from './routes/general journal/accrual-reversal/month/AccrualMonth.jsx';
import AccrualYear from './routes/general journal/accrual-reversal/year/AccrualYear.jsx';
import Folders from './routes/general journal/adjusting-entries/folders/Folders.jsx';
import Month from './routes/general journal/adjusting-entries/month/Month.jsx';
import Year from './routes/general journal/adjusting-entries/year/Year.jsx';

//ONE RDF Landing Page
import ExternalRedirect from './ExternalRedirect.jsx'; //Landing
import Redirect from './Redirect.jsx';

//ORIGINAL Landing Page
// const Landing = lazy(() => import('./Landing'));

// const ChangePassword = lazy(() => import('./routes/masterlist/ChangePassword'));

const Settings = lazy(() => import('./routes/settings/Settings.jsx'));

// Create a client
const queryClient = new QueryClient();

const App = () => {
  const { theme } = useFistoTheme();

  console.log(
    `%c
███████╗██╗ ██████╗████████╗ █████╗
██╔════╝██║██╔════╝╚══██╔══╝██╔══██╗%c
█████╗  ██║╚█████╗    ██║   ██║  ██║
██╔══╝  ██║ ╚═══██╗   ██║   ██║  ██║%c
██║     ██║██████╔╝   ██║   ╚█████╔╝
╚═╝     ╚═╝╚═════╝    ╚═╝    ╚════╝ %c

    Financial Statement Online
    
  `,
    `color: #6a4b9f`,
    `color: #543b7e`,
    `color: #47326a`,
    `color: #b9e4f3`,
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <QueryClientProvider client={queryClient}>
        <Router>
          <Routes>
            <Route exact strict path="*" element={<NotFound />} />
            <Route
              exact
              path="/sandbox"
              element={
                <FistoProvider>
                  <ReasonProvider>
                    <Sandbox />
                  </ReasonProvider>
                </FistoProvider>
              }
            />
            <Route
              exact
              path="/redirect"
              element={
                <SuspenseWrapper>
                  <Redirect />
                </SuspenseWrapper>
              }
            />
            <Route
              exact
              path="/"
              // element={
              //   <SuspenseWrapper>
              //     <Landing />
              //   </SuspenseWrapper>
              // }
              element={<ExternalRedirect url="https://one.rdfmis.com/login" />}
            />
            {/* <Route exact path="/user/change-password" element={<ProtectedRoute />}>
              <Route
                index
                exact
                strict
                element={
                  <SuspenseWrapper>
                    <ChangePassword />
                  </SuspenseWrapper>
                }
              />
            </Route> */}
            <Route exact path="/masterlist" element={<ProtectedRoute permission={0} />}>
              {masterlistConfig.map(({ path, component: Component, wrapper: Wrapper }) => (
                <Route
                  key={path}
                  exact
                  path={path}
                  element={
                    Wrapper ? (
                      <Wrapper>
                        <SuspenseWrapper>
                          <Component />
                        </SuspenseWrapper>
                      </Wrapper>
                    ) : (
                      <SuspenseWrapper>
                        <Component />
                      </SuspenseWrapper>
                    )
                  }
                />
              ))}
            </Route>
            <Route exact path="/request" element={<ProtectedRoute permission={1} />}>
              {
                //NORM REQUEST ROUTES
                requestConfig.map(({ index = false, path, component: Component, wrapper: Wrapper }) => (
                  <Route
                    index={index}
                    key={path}
                    exact
                    strict
                    path={path}
                    element={
                      Wrapper ? (
                        <Wrapper>
                          <SuspenseWrapper>
                            <Component />
                          </SuspenseWrapper>
                        </Wrapper>
                      ) : (
                        <SuspenseWrapper>
                          <Component />
                        </SuspenseWrapper>
                      )
                    }
                  />
                ))
              }
            </Route>
            <Route exact path="/request/managers_cheque" element={<ProtectedRoute permission={29} />}>
              {
                //MC REQUEST ROUTES
                managersChequeRequestConfig.map(({ index = false, path, component: Component, wrapper: Wrapper }) => (
                  <Route
                    index={index}
                    key={path}
                    exact
                    strict
                    path={path}
                    element={
                      Wrapper ? (
                        <Wrapper>
                          <SuspenseWrapper>
                            <Component />
                          </SuspenseWrapper>
                        </Wrapper>
                      ) : (
                        <SuspenseWrapper>
                          <Component />
                        </SuspenseWrapper>
                      )
                    }
                  />
                ))
              }
            </Route>
            {[
              ...documentsRouteConfig,
              ...voucheringRouteConfig,
              ...auditingRouteConfig,
              ...approvingRouteConfig,
              ...applicationRouteConfig,
              ...confidentialRouteConfig,
              ...chequeRouteConfig,
              ...counteringRouteConfig,
              ...reportRouteConfig,
            ].map(({ parent, child }) => {
              const { wrapper: Wrapper, component: Component } = child;
              return (
                <Route exact={parent.exact} path={parent.path} element={<parent.element permission={parent?.permission_id} />}>
                  <Route
                    index={child.index}
                    exact={child.exact}
                    strict
                    path={child.path}
                    element={
                      Wrapper ? (
                        <Wrapper>
                          <SuspenseWrapper>
                            <Component />
                          </SuspenseWrapper>
                        </Wrapper>
                      ) : (
                        <SuspenseWrapper>
                          <Component />
                        </SuspenseWrapper>
                      )
                    }
                  />
                </Route>
              );
            })}
            <Route exact path="/confidential/request" element={<ProtectedRoute permission={2} />}>
              {
                //CONFIDENTIAL REQUEST ROUTES
                confidentialRequestRouteConfig.map(({ index = false, path, component: Component, wrapper: Wrapper }) => (
                  <Route
                    index={index}
                    key={path}
                    exact
                    strict
                    path={path}
                    element={
                      Wrapper ? (
                        <Wrapper>
                          <SuspenseWrapper>
                            <Component />
                          </SuspenseWrapper>
                        </Wrapper>
                      ) : (
                        <SuspenseWrapper>
                          <Component />
                        </SuspenseWrapper>
                      )
                    }
                  />
                ))
              }
            </Route>

            {generalJournalRouteConfig.map(({ parent, child }) => {
              const { wrapper: Wrapper, component: Component } = child;
              const hasFolders = Boolean(parent.rootPath);

              return (
                <Route exact={parent.exact} path={parent.path} element={<parent.element permission={parent?.permission_id} />}>
                  {hasFolders ? (
                    <Route
                      index={child.index}
                      exact={child.exact}
                      strict
                      path={child.path}
                      element={
                        Wrapper ? (
                          <Wrapper>
                            <SuspenseWrapper>
                              <Component />
                            </SuspenseWrapper>
                          </Wrapper>
                        ) : (
                          <SuspenseWrapper>
                            <Component />
                          </SuspenseWrapper>
                        )
                      }
                    >
                      <Route
                        index
                        element={
                          <SuspenseWrapper>
                            <Folders rootPath={parent.rootPath} />
                          </SuspenseWrapper>
                        }
                      />
                      <Route
                        path=":year"
                        element={
                          <SuspenseWrapper>
                            <Year rootPath={parent.rootPath} />
                          </SuspenseWrapper>
                        }
                      />
                      <Route
                        path=":year/:month"
                        element={
                          <SuspenseWrapper>
                            <Month rootPath={parent.rootPath} />
                          </SuspenseWrapper>
                        }
                      />
                    </Route>
                  ) : (
                    <Route
                      index={child.index}
                      exact={child.exact}
                      strict
                      path={child.path}
                      element={
                        Wrapper ? (
                          <Wrapper>
                            <SuspenseWrapper>
                              <Component />
                            </SuspenseWrapper>
                          </Wrapper>
                        ) : (
                          <SuspenseWrapper>
                            <Component />
                          </SuspenseWrapper>
                        )
                      }
                    />
                  )}
                </Route>
              );
            })}
            {accrualReversalRouteConfig.map(({ parent, child }) => {
              const { wrapper: Wrapper, component: Component } = child;
              const hasFolders = Boolean(parent?.rootPath);

              return (
                <Route exact={parent.exact} path={parent.path} element={<parent.element permission={parent?.permission_id} />}>
                  {hasFolders ? (
                    <Route
                      index={child.index}
                      exact={child.exact}
                      strict
                      path={child.path}
                      element={
                        Wrapper ? (
                          <Wrapper>
                            <SuspenseWrapper>
                              <Component />
                            </SuspenseWrapper>
                          </Wrapper>
                        ) : (
                          <SuspenseWrapper>
                            <Component />
                          </SuspenseWrapper>
                        )
                      }
                    >
                      <Route
                        index
                        element={
                          <SuspenseWrapper>
                            <AccrualFolders />
                          </SuspenseWrapper>
                        }
                      />

                      <Route
                        path=":year"
                        element={
                          <SuspenseWrapper>
                            <AccrualYear />
                          </SuspenseWrapper>
                        }
                      />

                      <Route
                        path=":year/:month"
                        element={
                          <SuspenseWrapper>
                            <AccrualMonth />
                          </SuspenseWrapper>
                        }
                      />
                    </Route>
                  ) : (
                    <Route
                      index={child.index}
                      exact={child.exact}
                      strict
                      path={child.path}
                      element={
                        Wrapper ? (
                          <Wrapper>
                            <SuspenseWrapper>
                              <Component />
                            </SuspenseWrapper>
                          </Wrapper>
                        ) : (
                          <SuspenseWrapper>
                            <Component />
                          </SuspenseWrapper>
                        )
                      }
                    />
                  )}
                </Route>
              );
            })}
            <Route element={<ProtectedRoute permission={42} />}>
              <Route
                index
                exact
                strict
                path="/settings"
                element={
                  <SuspenseWrapper>
                    <Settings />
                  </SuspenseWrapper>
                }
              />
            </Route>
          </Routes>
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
